/**
 * Guards async work against stale results from superseded calls.
 *
 * When a request can be re-fired before the previous one resolves (rapid
 * typing, filter changes, re-renders), a slow earlier response can land after
 * a newer one and overwrite fresh state with stale data. Each track() returns
 * a token that becomes `isStale` as soon as a newer request for the same key
 * starts, so callers can bail out after an await instead of committing it.
 *
 * Use distinct keys to guard several concurrent requests from one instance.
 * Note: this only discards late results - it does not cancel the network
 * request (use an AbortController for that).
 */

export interface RequestToken {
  /** True once a newer request with the same key has been started. */
  readonly isStale: boolean;
  /** True while this is still the most recent tracked request for its key. */
  readonly isCurrent: boolean;
}

export const useLatestRequest = () => {
  const counters = new Map<PropertyKey, number>();

  const track = (key: PropertyKey = 'default'): RequestToken => {
    const id = (counters.get(key) ?? 0) + 1;
    counters.set(key, id);

    return {
      get isStale() {
        return id !== counters.get(key);
      },
      get isCurrent() {
        return id === counters.get(key);
      },
    };
  };

  /** Invalidate the in-flight request for a key without starting a new one (e.g. onUnmounted, reset). */
  const invalidate = (key: PropertyKey = 'default') => {
    counters.set(key, (counters.get(key) ?? 0) + 1);
  };

  return { track, invalidate };
};
